import { useState, useEffect, useCallback, type ReactNode } from 'react';

/* ----------------------------------------------------------------------------
   PWA install affordance.

   The app is fully installable (valid manifest + a registered service worker
   with a fetch handler), but browsers hide the install path in places most
   people never look:

     - Android / desktop Chrome fire a `beforeinstallprompt` event we can
       capture and replay from our own button — one tap, native sheet.
     - iOS Safari NEVER fires that event and has no programmatic install at
       all. The only path is Share → "Add to Home Screen", so for iOS we show
       short instructions instead.
     - Other browsers (Firefox, etc.) vary; we fall back to generic guidance.

   `usePwaInstall` centralises the detection; `InstallAppControl` renders a
   trigger (styled by the caller via `className`) plus a small instructions
   sheet for the platforms that can't be prompted programmatically.
   -------------------------------------------------------------------------- */

// The `beforeinstallprompt` event isn't in the TS DOM lib yet.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    // iOS Safari exposes this non-standard flag when launched from the home screen.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function detectIOS(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iOSDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ masquerades as desktop Safari — catch it via touch points.
  const iPadOS = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

export function usePwaInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const isIOS = detectIOS();

  useEffect(() => {
    // Capture the install event so we can replay it from our own button
    // instead of relying on the browser's hidden menu item.
    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    // Once installed, drop the prompt and hide the affordance.
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Keep standalone state honest if the display-mode flips at runtime.
    const mq = window.matchMedia?.('(display-mode: standalone)');
    const onDisplayChange = () => setIsStandalone(detectStandalone());
    mq?.addEventListener?.('change', onDisplayChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      mq?.removeEventListener?.('change', onDisplayChange);
    };
  }, []);

  // Fire the native install sheet. Returns the user's choice, or null when no
  // captured prompt is available (iOS, or before the event has fired).
  const promptInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!deferred) return null;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // The event is single-use — clear it so the button stops offering a stale prompt.
    setDeferred(null);
    return outcome;
  }, [deferred]);

  return {
    /** True when a native install prompt is ready to replay (Android / desktop Chrome). */
    canPrompt: deferred !== null,
    /** Running as an installed app already — hide the affordance entirely. */
    isStandalone,
    /** iOS device — no programmatic install, show Share-sheet instructions. */
    isIOS,
    promptInstall,
  };
}

/** Small "Add to Home Screen" instructions sheet for platforms we can't prompt. */
function InstallHelpSheet({ isIOS, onClose }: { isIOS: boolean; onClose: () => void }) {
  // Lock body scroll while the sheet is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-label="Install EntreDash"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(15, 23, 20, 0.45)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: 'clamp(0px, 4vw, 24px)',
        animation: 'fadeIn 180ms ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(440px, 100%)',
          background: 'var(--surface-page)',
          borderRadius: '20px 20px 0 0',
          padding: '24px 24px calc(24px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -12px 48px rgba(15, 23, 20, 0.22)',
          animation: 'sheetUp 240ms ease',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--f-serif, Sentient, Georgia, serif)',
            fontSize: 22,
            color: 'var(--ink)',
            marginBottom: 6,
          }}
        >
          Install EntreDash
        </div>
        <p
          style={{
            fontFamily: 'var(--f-sans)',
            fontSize: 15,
            lineHeight: 1.6,
            color: 'var(--text-decorative, rgba(15,23,20,0.66))',
            margin: '0 0 18px',
          }}
        >
          Add EntreDash to your home screen for a full-screen, app-like experience that
          opens in one tap.
        </p>

        {isIOS ? (
          <ol style={stepListStyle}>
            <li style={stepStyle}>
              Tap the <strong>Share</strong> icon
              <ShareGlyph /> in Safari&rsquo;s toolbar.
            </li>
            <li style={stepStyle}>
              Scroll down and choose <strong>Add to Home Screen</strong>.
            </li>
            <li style={stepStyle}>
              Tap <strong>Add</strong> in the top corner. EntreDash appears on your
              home screen.
            </li>
          </ol>
        ) : (
          <ol style={stepListStyle}>
            <li style={stepStyle}>
              Open your browser&rsquo;s menu (the <strong>⋮</strong> or <strong>⋯</strong>{' '}
              icon).
            </li>
            <li style={stepStyle}>
              Choose <strong>Install app</strong> or <strong>Add to Home Screen</strong>.
            </li>
            <li style={stepStyle}>Confirm, and EntreDash installs like a native app.</li>
          </ol>
        )}

        <button
          type="button"
          onClick={onClose}
          style={{
            marginTop: 22,
            width: '100%',
            boxSizing: 'border-box',
            padding: 14,
            borderRadius: 999,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--f-sans)',
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--parchment)',
            background: 'var(--heartwood)',
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

const stepListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: 22,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};
const stepStyle: React.CSSProperties = {
  fontFamily: 'var(--f-sans)',
  fontSize: 15,
  lineHeight: 1.5,
  color: 'var(--ink)',
};

function ShareGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'inline', verticalAlign: '-2px', margin: '0 3px', opacity: 0.75 }}
      aria-hidden="true"
    >
      <path d="M12 16V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}

/**
 * A self-contained install trigger. Renders nothing once the app is already
 * installed (standalone). Otherwise it fires the native prompt when one is
 * available, and falls back to an instructions sheet on iOS / browsers that
 * don't expose a programmatic prompt.
 *
 * The caller styles the trigger via `className` and provides the label as
 * `children`, so the same control drops into the nav drawer, the footer, or
 * anywhere else.
 */
export function InstallAppControl({
  className,
  children,
  onActivate,
}: {
  className?: string;
  children?: ReactNode;
  /** Fires after the control does its thing — e.g. to close an open drawer. */
  onActivate?: () => void;
}) {
  const { canPrompt, isStandalone, isIOS, promptInstall } = usePwaInstall();
  const [showHelp, setShowHelp] = useState(false);

  // Already installed — nothing to offer.
  if (isStandalone) return null;

  const handleClick = async () => {
    if (canPrompt) {
      // Native path: fire the OS install sheet, then close the host drawer —
      // the OS UI takes over, so there's nothing left to show here.
      await promptInstall();
      onActivate?.();
      return;
    }
    // Instructions path (iOS, or before the event fires): show our own sheet.
    // Do NOT close the host here — this control lives inside the drawer/sidebar
    // subtree, and closing it would unmount the sheet along with it. The host
    // is dismissed when the sheet itself is closed (below).
    setShowHelp(true);
  };

  const closeHelp = () => {
    setShowHelp(false);
    // Now it's safe to collapse the host drawer/sidebar too.
    onActivate?.();
  };

  return (
    <>
      <button type="button" className={className} onClick={handleClick}>
        {children ?? 'Install app'}
      </button>
      {showHelp && <InstallHelpSheet isIOS={isIOS} onClose={closeHelp} />}
    </>
  );
}
